import axios from 'axios';
import { parseApiError } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Secure Exam Mode: while a secure exam is in progress the student flow
    // keeps the server-issued secure-session token here; attach it to the
    // exam endpoints the backend gates (start / save / submit).
    try {
      const secure = sessionStorage.getItem('secureExamToken');
      const url = config.url || '';
      if (secure && (url.includes('/assessments/attempts/') || /\/assessments\/[^/]+\/start$/.test(url))) {
        config.headers['X-Secure-Exam-Token'] = secure;
      }
    } catch { /* sessionStorage unavailable */ }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Centralized developer-facing logging + safe-message parsing for every
    // failed API call, regardless of whether the call site does its own
    // (richer, context-specific) handleApiError() call on top of this.
    // Never surface this raw detail in the UI — only console/devtools.
    error.parsedError = parseApiError(error);
    // eslint-disable-next-line no-console
    console.error('[API]', {
      method: error.config?.method,
      url: error.config?.url,
      status: error.response?.status,
      code: error.parsedError.code,
      requestId: error.parsedError.requestId,
      responseData: error.response?.data,
      message: error.message,
    });

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
        processQueue(null, access_token);
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => publicApi.post('/auth/login', credentials),
  loginByRole: (data) => publicApi.post('/auth/login/role', data),
  register: (data) => publicApi.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: (token) => publicApi.post('/auth/refresh', { refresh_token: token }),
  loadUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (email) => publicApi.post('/auth/forgot-password', { email }),
  resetPassword: (data) => publicApi.post('/auth/reset-password', data),
};

export const schoolAPI = {
  getSchools: () => api.get('/schools'),
  getSchool: (id) => api.get(`/schools/${id}`),
  updateSchool: (id, data) => api.put(`/schools/${id}`, data),
  getConfig: (id) => api.get(`/schools/${id}/config`),
  updateConfig: (id, data) => api.put(`/schools/${id}/config`, data),
  getBranches: (id) => api.get(`/schools/${id}/branches`),
  createBranch: (id, data) => api.post(`/schools/${id}/branches`, data),
  // Generic key/value config store (matches the actual GET/PUT /configs API).
  listConfigs: (id) => api.get(`/schools/${id}/configs`),
  setConfig: (id, key, value) =>
    api.put(`/schools/${id}/configs/${key}`, null, { params: { config_value: value } }),
};

export const studentAPI = {
  fetchStudents: (params) => api.get('/students', { params }),
  fetchStudent: (id) => api.get(`/students/${id}`),
  getMyProfile: () => api.get('/students/me'),
  createStudent: (data) => api.post('/students', data),
  updateStudent: (id, data) => api.put(`/students/${id}`, data),
  deleteStudent: (id) => api.delete(`/students/${id}`),
  promoteStudent: (id, data) => api.post(`/students/${id}/promote`, data),
  transferStudent: (id, data) => api.post(`/students/${id}/transfer`, data),
  resetAdmissionNumber: (id, data) => api.post(`/students/${id}/admission-number`, data),
  regenerateAdmissionNumbers: (data) => api.post('/students/admission-numbers/regenerate', data),
  uploadDocument: (id, data) => api.post(`/students/${id}/documents`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStudentDocuments: (id) => api.get(`/students/${id}/documents`),
  getStudentParents: (id) => api.get(`/students/${id}/parents`),
  getStudentFees: (id) => api.get(`/students/${id}/fees`),
  getStudentAssignments: (studentId) => api.get(`/assignments/student/${studentId}`),
  getStudentAssignmentDashboard: (studentId) => api.get(`/assignments/dashboard/${studentId}`),
  submitAssignment: (assignmentId, formData) => api.post(`/assignments/${assignmentId}/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStudentQuizzes: (studentId) => api.get(`/quizzes/student/${studentId}`),
  getStudentQuizDashboard: () => api.get('/quizzes/dashboard'),
  startQuiz: (quizId, data) => api.post(`/quizzes/${quizId}/start`, data),
  submitQuizAttempt: (attemptId, data) => api.post(`/quizzes/attempts/${attemptId}/submit`, data),
  getQuizAttempt: (attemptId) => api.get(`/quizzes/attempts/${attemptId}`),
  getQuizAttemptReview: (attemptId) => api.get(`/quizzes/attempts/${attemptId}/review`),
  exportCallingRegister: (startMonth, startYear, endMonth, endYear, classId) => api.get('/students/export/calling-register', {
    params: {
      start_month: startMonth, start_year: startYear,
      end_month: endMonth, end_year: endYear,
      class_id: classId || undefined,
    },
    responseType: 'blob',
  }),
};

export const recurringFeeAPI = {
  fetchRecurringFees: (params) => api.get('/students/recurring-fees', { params }),
  fetchMyRecurringFees: () => api.get('/students/me/recurring-fees'),
};

export const teacherAPI = {
  fetchTeachers: (params) => api.get('/teachers', { params }),
  fetchTeacher: (id) => api.get(`/teachers/${id}`),
  createTeacher: (data) => api.post('/teachers', data),
  updateTeacher: (id, data) => api.put(`/teachers/${id}`, data),
  deleteTeacher: (id) => api.delete(`/teachers/${id}`),
  assignSubject: (id, data) => api.post(`/teachers/${id}/subjects`, data),
  assignClass: (id, data) => api.post(`/teachers/${id}/classes`, data),
  getTeacherTimetable: (id) => api.get(`/teachers/${id}/timetable`),
  getTeacherStudents: (id) => api.get(`/teachers/${id}/students`),
};

export const timeSlotAPI = {
  fetchTimeSlots: () => api.get('/attendance/time-slots'),
  createTimeSlot: (data) => api.post('/attendance/time-slots', data),
  updateTimeSlot: (id, data) => api.put(`/attendance/time-slots/${id}`, data),
  deleteTimeSlot: (id) => api.delete(`/attendance/time-slots/${id}`),
};

export const attendanceAPI = {
  fetchAttendance: (params) => api.get('/attendance', { params }),
  markAttendance: (data) => api.post('/attendance/mark', data),
  markBulkAttendance: (data) => api.post('/attendance/bulk', data),
  getAttendanceReport: (params) => api.get('/attendance/report', { params }),
  getStudentAttendance: (id, params) => api.get(`/attendance/student/${id}/records`, { params }),
  getStudentAttendanceSummary: (id, params) => api.get(`/attendance/student/${id}/summary`, { params }),
  getTodayAttendance: (params) => {
    const classId = params?.class_id || params?.class;
    if (classId) {
      const queryParams = { attendance_date: params?.date || undefined };
      return api.get(`/attendance/class/${classId}`, { params: queryParams });
    }
    return api.get('/attendance', { params });
  },
  getAttendanceStats: (params) => api.get('/attendance/stats', { params }),
  previewStudentPenalties: (params) => api.get('/attendance/penalties/students/preview', { params }),
  deleteStudentPenalties: (params) => api.delete('/attendance/penalties/students', { params }),
};

export const academicAPI = {
  fetchAcademicYears: (params) => api.get('/academic/academic-years', { params }),
  createAcademicYear: (data) => api.post('/academic/academic-years', data),
  updateAcademicYear: (id, data) => api.put(`/academic/academic-years/${id}/activate`, data),
  fetchTerms: (params) => api.get('/academic/terms', { params }),
  createTerm: (data) => api.post('/academic/terms', data),
  updateTerm: (id, data) => api.put(`/academic/terms/${id}`, data),
  fetchClasses: (params) => api.get('/academic/classes', { params }),
  createClass: (data) => api.post('/academic/classes', data),
  updateClass: (id, data) => api.put(`/academic/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/academic/classes/${id}`),
  fetchStreams: (params) => api.get('/academic/streams', { params }),
  createStream: (data) => api.post('/academic/streams', data),
  fetchSubjects: (params) => api.get('/academic/subjects', { params }),
  createSubject: (data) => api.post('/academic/subjects', data),
  updateSubject: (id, data) => api.put(`/academic/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/academic/subjects/${id}`),
  fetchTimetable: (params) => api.get('/academic/timetable', { params }),
  createTimetableEntry: (data) => api.post('/academic/timetable', data),
  updateTimetableEntry: (id, data) => api.put(`/academic/timetable/${id}`, data),
  deleteTimetableEntry: (id) => api.delete(`/academic/timetable/${id}`),
};

export const assignmentAPI = {
  fetchAssignments: (params) => api.get('/assignments', { params }),
  createAssignment: (data) => api.post('/assignments', data),
  fetchAssignment: (id) => api.get(`/assignments/${id}`),
  updateAssignment: (id, data) => api.put(`/assignments/${id}`, data),
  deleteAssignment: (id) => api.delete(`/assignments/${id}`),
  updateAssignmentStatus: (id, data) => api.post(`/assignments/${id}/status`, data),
  uploadAssignmentFiles: (id, formData) => api.post(`/assignments/${id}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteAssignmentFile: (fileId) => api.delete(`/assignments/files/${fileId}`),
  getAssignmentFiles: (id) => api.get(`/assignments/${id}/files`),
  getAssignmentSubmissions: (id) => api.get(`/assignments/${id}/submissions`),
  getSubmission: (submissionId) => api.get(`/assignments/submissions/${submissionId}`),
  gradeSubmission: (submissionId, data) => api.put(`/assignments/submissions/${submissionId}/grade`, data),
  returnSubmission: (submissionId) => api.put(`/assignments/submissions/${submissionId}/return`),
  getTeacherAssignmentDashboard: () => api.get('/assignments/teacher/dashboard'),
};

export const quizAPI = {
  fetchQuizzes: (params) => api.get('/quizzes', { params }),
  createQuiz: (data) => api.post('/quizzes', data),
  fetchQuiz: (id) => api.get(`/quizzes/${id}`),
  updateQuiz: (id, data) => api.put(`/quizzes/${id}`, data),
  deleteQuiz: (id) => api.delete(`/quizzes/${id}`),
  updateQuizStatus: (id, data) => api.post(`/quizzes/${id}/status`, data),
  addQuestion: (quizId, data) => api.post(`/quizzes/${quizId}/questions`, data),
  updateQuestion: (questionId, data) => api.put(`/quizzes/questions/${questionId}`, data),
  deleteQuestion: (questionId) => api.delete(`/quizzes/questions/${questionId}`),
  getQuizQuestions: (quizId) => api.get(`/quizzes/${quizId}/questions`),
  getTeacherQuizDashboard: () => api.get('/quizzes/teacher/dashboard'),
  getAttempts: (quizId) => api.get(`/quizzes/${quizId}/attempts`),
  getQuizAttemptAnswers: (attemptId) => api.get(`/quizzes/attempts/${attemptId}/answers`),
  gradeManualQuestions: (attemptId, data) => api.put(`/quizzes/attempts/${attemptId}/grade-manual`, data),
};

export const examAPI = {
  fetchExams: (params) => api.get('/exams', { params }),
  createExam: (data) => api.post('/exams', data),
  updateExam: (id, data) => api.put(`/exams/${id}`, data),
  deleteExam: (id) => api.delete(`/exams/${id}`),
  enterResult: (id, data) => api.post(`/exams/${id}/results`, data),
  enterBulkResults: (id, data) => api.post(`/exams/${id}/results/bulk`, data),
  fetchResults: (id, params) => api.get(`/exams/${id}/results`, { params }),
  getExam: (id) => api.get(`/exams/${id}`),
  publishResults: (id) => api.post(`/exams/${id}/publish`),
  getExamSchedules: (params) => api.get('/exams/schedules', { params }),
  createExamSchedule: (data) => api.post('/exams/schedules', data),
};

export const resultAPI = {
  fetchResults: (params) => api.get('/results', { params }),
  computeResults: (data) => api.post('/results/compute', data),
  generateReportCards: (data) => api.post('/results/report-cards', data, {
    responseType: 'blob',
  }),
  fetchRankings: (params) => api.get('/results/rankings', { params }),
  getStudentResults: (id, params) => api.get(`/results/student/${id}`, { params }),
  getClassResults: (id, params) => api.get(`/results/class/${id}`, { params }),
  getResultAnalytics: (params) => api.get('/results/analytics', { params }),
  downloadReportCard: (studentId, examId) =>
    api.get(`/results/report-card/${studentId}/${examId}`, {
      responseType: 'blob',
    }),
};

// Progressive Reports - REB-style term report cards built from the online
// assessment marks. Eligibility (READY) -> generate -> review -> approve ->
// publish -> student/parent view.
export const progressiveReportAPI = {
  listPeriods: (params) => api.get('/progressive-reports/periods', { params }),
  createPeriod: (data) => api.post('/progressive-reports/periods', data),
  getPeriod: (id) => api.get(`/progressive-reports/periods/${id}`),
  updatePeriod: (id, data) => api.patch(`/progressive-reports/periods/${id}`, data),
  getPeriodAssessments: (id) => api.get(`/progressive-reports/periods/${id}/assessments`),
  setSubjectMap: (id, rows) => api.put(`/progressive-reports/periods/${id}/subject-map`, { rows }),
  getRoster: (id, params) => api.get(`/progressive-reports/periods/${id}/students`, { params }),
  setEligibility: (id, student_ids, status) =>
    api.post(`/progressive-reports/periods/${id}/eligibility`, { student_ids, status }),
  previewStudent: (id, studentId) =>
    api.get(`/progressive-reports/periods/${id}/preview/${studentId}`),
  generate: (id, data) => api.post(`/progressive-reports/periods/${id}/generate`, data),
  publishAll: (id) => api.post(`/progressive-reports/periods/${id}/publish-all`),
  downloadAll: (id) => api.get(`/progressive-reports/periods/${id}/download-all`, { responseType: 'blob' }),
  // Whole class as ONE print-ready PDF (one print job). onDownloadProgress
  // lets the page show real progress for a large class.
  printAll: (id, params, onDownloadProgress) => api.get(`/progressive-reports/periods/${id}/print-all`, {
    params, responseType: 'blob', onDownloadProgress, timeout: 300000,
  }),
  listReports: (params) => api.get('/progressive-reports/reports', { params }),
  getReport: (id) => api.get(`/progressive-reports/reports/${id}`),
  getReportPrint: (id) => api.get(`/progressive-reports/reports/${id}/print`),
  downloadReportPdf: (id) => api.get(`/progressive-reports/reports/${id}/pdf`, { responseType: 'blob' }),
  reviewReport: (id, data) => api.post(`/progressive-reports/reports/${id}/review`, data || {}),
  approveReport: (id, data) => api.post(`/progressive-reports/reports/${id}/approve`, data || {}),
  publishReport: (id, data) => api.post(`/progressive-reports/reports/${id}/publish`, data || {}),
  archiveReport: (id, data) => api.post(`/progressive-reports/reports/${id}/archive`, data || {}),
  regenerateReport: (id) => api.post(`/progressive-reports/reports/${id}/regenerate`),
  myReports: () => api.get('/progressive-reports/me/reports'),
  myChildrenReports: () => api.get('/progressive-reports/me/children/reports'),
  // Weekly / Midterm progress views - dynamic, no publish gate.
  weeklyView: (periodId, studentId) => api.get(`/progressive-reports/periods/${periodId}/weekly/${studentId}`),
  midtermView: (periodId, studentId) => api.get(`/progressive-reports/periods/${periodId}/midterm/${studentId}`),
  myWeekly: () => api.get('/progressive-reports/me/weekly'),
  myMidterm: () => api.get('/progressive-reports/me/midterm'),
  childWeekly: (studentId) => api.get('/progressive-reports/me/children/weekly', { params: { student_id: studentId } }),
  childMidterm: (studentId) => api.get('/progressive-reports/me/children/midterm', { params: { student_id: studentId } }),
  // Week reports (WEEK-type periods) - published documents, own lifecycle.
  listWeeks: (params) => api.get('/progressive-reports/weeks', { params }),
  readinessDashboard: (params) => api.get('/progressive-reports/report-readiness', { params }),
  forceCloseWeek: (classId, weekNumber, params) =>
    api.post(`/progressive-reports/report-readiness/${classId}/weeks/${weekNumber}/force-close`, null, { params }),
  revertCloseWeek: (classId, weekNumber) =>
    api.post(`/progressive-reports/report-readiness/${classId}/weeks/${weekNumber}/revert-close`),
};

export const payrollAPI = {
  fetchPayrolls: (params) => api.get('/payroll', { params }),
  processPayroll: (data) => api.post('/payroll/process', data),
  processAllPayroll: (data) => api.post('/payroll/process-all', data),
  createAllowance: (data) => api.post('/payroll/allowances', data),
  createDeduction: (data) => api.post('/payroll/deductions', data),
  fetchPayslip: (id) => api.get(`/payroll/payslip/${id}`),
  fetchAllPayslips: (params) => api.get('/payroll/payslips', { params }),
  approvePayroll: (id) => api.put(`/payroll/${id}/approve`),
  payPayroll: (id) => api.put(`/payroll/${id}/pay`),
  getPayrollSummary: (params) => api.get('/payroll/summary', { params }),
};

export const financeAPI = {
  fetchFeeStructures: (params) => api.get('/finance/fee-structures', { params }),
  createFeeStructure: (data) => api.post('/finance/fee-structures', data),
  updateFeeStructure: (id, data) => api.put(`/finance/fee-structures/${id}`, data),
  deleteFeeStructure: (id) => api.delete(`/finance/fee-structures/${id}`),
  fetchStudentFees: (params) => api.get('/finance/student-fees', { params }),
  createPayment: (data) => api.post('/finance/payments', data),
  fetchPayments: (params) => api.get('/finance/payments', { params }),
  updatePaymentPromise: (id, data) => api.put(`/finance/payments/${id}/promise`, data),
  fetchPendingPayments: (params) => api.get('/finance/payments/pending', { params }),
  submitPayment: (formData) => api.post('/finance/payments/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  verifyPayment: (id, data) => api.put(`/finance/payments/${id}/verify`, data),
  revertPayment: (id, data) => api.put(`/finance/payments/${id}/revert`, data),
  fetchRevertPreview: (params) => api.get('/finance/payment-revert-preview', { params }),
  fetchExpenses: (params) => api.get('/finance/expenses', { params }),
  createExpense: (data) => api.post('/finance/expenses', data),
  updateExpense: (id, data) => api.put(`/finance/expenses/${id}`, data),
  fetchIncome: (params) => api.get('/finance/income', { params }),
  createIncome: (data) => api.post('/finance/income', data),
  fetchReports: (params) => api.get('/finance/reports', { params }),
  getDashboardStats: (params) => api.get('/finance/dashboard', { params }),
  getFeeDashboard: (params) => api.get('/finance/dashboard', { params }),
  fetchDefaulters: (params) => api.get('/finance/defaulters', { params }),
  fetchReminders: () => api.get('/finance/reminders'),
  getStudentFeeProfile: (studentId) => api.get(`/finance/student-profile/${studentId}`),
  getMonthlyReport: (params) => api.get('/finance/reports/monthly', { params }),
  generateInvoice: (data) => api.post('/finance/invoices', data),
  getInvoice: (id) => api.get(`/finance/invoices/${id}`),
  fetchInvoices: (params) => api.get('/finance/invoices', { params }),
};

export const paymentReasonAPI = {
  fetchAll: (params) => api.get('/payment-reasons', { params }),
  review: (id, data) => api.post(`/payment-reasons/${id}/review`, data),
};

export const libraryAPI = {
  fetchBooks: (params) => api.get('/library', { params }),
  createBook: (data) => api.post('/library', data),
  updateBook: (id, data) => api.put(`/library/${id}`, data),
  deleteBook: (id) => api.delete(`/library/${id}`),
  borrowBook: (data) => api.post('/library/borrow', data),
  returnBook: (id, data) => api.put(`/library/return/${id}`, data),
  fetchBorrowings: (params) => api.get('/library/borrows', { params }),
  getBook: (id) => api.get(`/library/${id}`),
  searchBooks: (query) => api.get('/library', { params: { search: query } }),
  getLibraryStats: () => api.get('/library/stats'),
  getOverdueBooks: (params) => api.get('/library/overdue', { params }),
};

export const transportAPI = {
  fetchRoutes: (params) => api.get('/transport/routes', { params }),
  createRoute: (data) => api.post('/transport/routes', data),
  updateRoute: (id, data) => api.put(`/transport/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/transport/routes/${id}`),
  fetchStops: (params) => api.get('/transport/stops', { params }),
  createStop: (data) => api.post('/transport/stops', data),
  updateStop: (id, data) => api.put(`/transport/stops/${id}`, data),
  deleteStop: (id) => api.delete(`/transport/stops/${id}`),
  assignStudent: (data) => api.post('/transport/assign', data),
  unassignStudent: (id) => api.delete(`/transport/assign/${id}`),
  getRouteStudents: (id) => api.get(`/transport/routes/${id}/students`),
  getTransportStats: () => api.get('/transport/stats'),
  trackVehicle: (id) => api.get(`/transport/vehicles/${id}/track`),
};

export const hostelAPI = {
  fetchDormitories: (params) => api.get('/hostel/dormitories', { params }),
  createDormitory: (data) => api.post('/hostel/dormitories', data),
  updateDormitory: (id, data) => api.put(`/hostel/dormitories/${id}`, data),
  deleteDormitory: (id) => api.delete(`/hostel/dormitories/${id}`),
  fetchRooms: (params) => api.get('/hostel/rooms', { params }),
  createRoom: (data) => api.post('/hostel/rooms', data),
  updateRoom: (id, data) => api.put(`/hostel/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/hostel/rooms/${id}`),
  allocateRoom: (data) => api.post('/hostel/allocate', data),
  deallocateRoom: (id) => api.delete(`/hostel/allocate/${id}`),
  getHostelStats: () => api.get('/hostel/stats'),
  getRoomOccupants: (id) => api.get(`/hostel/rooms/${id}/occupants`),
};

export const pushSubscriptionAPI = {
  getVapidPublicKey: () => api.get('/push-subscriptions/vapid-public-key'),
  list: () => api.get('/push-subscriptions'),
  subscribe: (data) => api.post('/push-subscriptions', data),
  unsubscribe: (id) => api.delete(`/push-subscriptions/${id}`),
};

export const notificationAPI = {
  fetchNotifications: (params) => api.get('/notifications', { params }),
  sendNotification: (data) => api.post('/notifications', data),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  acknowledge: (id) => api.put(`/notifications/${id}/acknowledge`),
  markAllRead: () => api.put('/notifications/read-all'),
  markAllReadByCategory: (category) => api.put(`/notifications/read-all/${category}`),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  fetchPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  fetchCategoryPreferences: () => api.get('/notifications/preferences/categories'),
  updateCategoryPreference: (data) => api.put('/notifications/preferences/categories', data),
  resetCategoryPreferences: () => api.put('/notifications/preferences/categories/reset'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  getStats: () => api.get('/notifications/stats'),
};

export const disciplineAPI = {
  fetchRecords: (params) => api.get('/discipline', { params }),
  createRecord: (data) => api.post('/discipline', data),
  updateRecord: (id, data) => api.put(`/discipline/${id}`, data),
  resolveRecord: (id, data) => api.put(`/discipline/${id}/resolve`, data),
  getRecord: (id) => api.get(`/discipline/${id}`),
  getStudentRecords: (studentId, params) =>
    api.get(`/discipline/student/${studentId}`, { params }),
  getDisciplineStats: (params) => api.get('/discipline/stats', { params }),
};

export const aiAPI = {
  fetchPredictions: (params) => api.get('/ai/predictions', { params }),
  fetchAnalytics: (params) => api.get('/ai/analytics', { params }),
  generateReportComments: (data) => api.post('/ai/report-comments', data),
  fetchAnomalies: (params) => api.get('/ai/anomalies', { params }),
  getPerformancePrediction: (studentId) =>
    api.get(`/ai/predictions/performance/${studentId}`),
  getDropoutRisk: (params) => api.get('/ai/predictions/dropout-risk', { params }),
  getAttendanceInsights: (params) => api.get('/ai/analytics/attendance', { params }),
  getPerformanceAnalytics: (params) => api.get('/ai/analytics/performance', { params }),
  getFinancialForecast: (params) => api.get('/ai/analytics/financial', { params }),
  getSmartSuggestions: (params) => api.get('/ai/suggestions', { params }),
  generateLessonPlan: (data) => api.post('/ai/lesson-plan', data),
  generateQuestions: (data) => api.post('/ai/generate-questions', data),
  analyzeText: (data) => api.post('/ai/analyze-text', data),
};

export const parentAttendanceAPI = {
  getChildrenAttendance: () => api.get('/attendance/parent/children'),
  getChildAttendanceDetail: (studentId, params) => api.get(`/attendance/parent/child/${studentId}/attendance`, { params }),
  getChildAttendanceAnalytics: (studentId) => api.get(`/attendance/parent/child/${studentId}/analytics`),
};

export const attendancePasswordAPI = {
  getStatus: () => publicApi.get('/attendance/password/status'),
  setup: (data) => api.post('/attendance/password/setup', data),
  validate: (data) => publicApi.post('/attendance/password/validate', data),
  disable: () => api.delete('/attendance/password'),
};

export const publicFaceAPI = {
  recognize: (formData, config = {}) => publicApi.post('/face/public/recognize', formData, {
    timeout: 60000,
    ...config,
  }),
};

export const faceAPI = {
  registerFace: (data) => api.post('/face/register', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  verifyFace: (data) => api.post('/face/verify', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  validateFace: (data) => api.post('/face/validate', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  }),
  checkQuality: (data) => api.post('/face/quality', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  }),
  getRegisteredFaces: (params) => api.get('/face/registered', { params }),
  deleteFace: (id) => api.delete(`/face/${id}`),
};

export const auditAPI = {
  fetchLogs: (params) => api.get('/audit/logs', { params }),
  getLog: (id) => api.get(`/audit/logs/${id}`),
  fetchLogins: (params) => api.get('/audit/logins', { params }),
  exportLogs: (params) => api.get('/audit/logs/export', { params, responseType: 'blob' }),
  // Who logged in, hour by hour (local time). Pass -new Date().getTimezoneOffset()
  // as tz_offset_minutes so buckets align to the admin's local clock.
  loginActivity: (params) => api.get('/audit/login-activity', { params }),
};

export const teacherLatenessAPI = {
  // Configuration
  getLatenessConfig: () => api.get('/teacher-lateness/config'),
  updateLatenessConfig: (data) => api.put('/teacher-lateness/config', data),

  // Holidays
  getHolidays: () => api.get('/teacher-lateness/holidays'),
  createHoliday: (data) => api.post('/teacher-lateness/holidays', data),
  deleteHoliday: (id) => api.delete(`/teacher-lateness/holidays/${id}`),

  // Deduction Policies
  getDeductionPolicies: (params) => api.get('/teacher-lateness/deduction-policies', { params }),
  createDeductionPolicy: (data) => api.post('/teacher-lateness/deduction-policies', data),
  updateDeductionPolicy: (id, data) => api.put(`/teacher-lateness/deduction-policies/${id}`, data),
  deleteDeductionPolicy: (id) => api.delete(`/teacher-lateness/deduction-policies/${id}`),

  // Teacher Attendance (admin marking)
  markTeacherAttendance: (data) => api.post('/teacher-lateness/attendance/mark', data),
  getTeacherAttendance: (params) => api.get('/teacher-lateness/attendance', { params }),

  // Teacher Dashboard
  getLatenessDashboard: () => api.get('/teacher-lateness/dashboard'),

  // Admin Summary & Reports
  getAdminLatenessSummary: () => api.get('/teacher-lateness/summary/admin'),
  getLatenessReport: (params) => api.get('/teacher-lateness/reports/lateness', { params }),

  // Penalty cleanup
  previewTeacherPenalties: (params) => api.get('/teacher-lateness/penalties/teachers/preview', { params }),
  deleteTeacherPenalties: (params) => api.delete('/teacher-lateness/penalties/teachers', { params }),

  // Credentials Management
  listCredentials: (params) => api.get('/admin/credentials', { params }),
  setPassword: (data) => api.post('/admin/credentials/set-password', data),
  deleteUser: (id) => api.delete(`/users/${id}`),
};

export const homeworkAPI = {
  getTeacherDashboard: () => api.get('/homework/teacher/dashboard'),
  listHomework: (params) => api.get('/homework', { params }),
  createHomework: (data) => api.post('/homework', data),
  getHomework: (id) => api.get(`/homework/${id}`),
  updateHomework: (id, data) => api.put(`/homework/${id}`, data),
  deleteHomework: (id) => api.delete(`/homework/${id}`),
  // File uploads need more than the default 30s timeout - a large scanned
  // PDF worksheet over a slow connection can take minutes to upload, and
  // the default was cutting the request off client-side before it finished
  // (surfaced server-side as nginx logging a 499 "client closed request").
  // Matches nginx's client_max_body_size (100M) / *_timeout (300s) for
  // these paths in deploy/nginx-bmcl.conf - keep both in sync.
  uploadHomeworkPdf: (homeworkId, formData) => api.post(`/homework/${homeworkId}/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  uploadQuestionImage: (questionId, formData) => api.post(`/homework/questions/${questionId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  submitHomeworkAnswer: (homeworkId, formData) => api.post(`/homework/${homeworkId}/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
  }),
  getAssignments: (homeworkId, params) => api.get(`/homework/${homeworkId}/assignments`, { params }),
  checkHomework: (homeworkId, data) => api.post(`/homework/${homeworkId}/check`, data),
  checkHomeworkBulk: (homeworkId, data) => api.post(`/homework/${homeworkId}/check-bulk`, data),
  getStudentHomework: (params) => api.get('/homework/student/list', { params }),
  getStudentHomeworkDetail: (homeworkId) => api.get(`/homework/student/${homeworkId}`),
  getStudentHistory: () => api.get('/homework/student/history'),
  getPerformance: (params) => api.get('/homework/reports/performance', { params }),
};

export const assessmentAPI = {
  // The list view has no pagination and shows every assessment; ask for a high
  // limit so older ones aren't cut off (a caller can still override).
  fetchAssessments: (params) => api.get('/assessments', { params: { limit: 2000, ...params } }),
  createAssessment: (data) => api.post('/assessments', data),
  getAssessment: (id) => api.get(`/assessments/${id}`),
  updateAssessment: (id, data) => api.put(`/assessments/${id}`, data),
  deleteAssessment: (id) => api.delete(`/assessments/${id}`),
  publishAssessment: (id) => api.post(`/assessments/${id}/publish`),
  closeAssessment: (id) => api.post(`/assessments/${id}/close`),
  archiveAssessment: (id) => api.post(`/assessments/${id}/archive`),
  duplicateQuestion: (qId) => api.post(`/assessments/questions/${qId}/duplicate`),
  reorderQuestions: (data) => api.put('/assessments/questions/reorder', data),
  getQuestions: (id) => api.get(`/assessments/${id}/questions`),
  addQuestion: (id, data) => api.post(`/assessments/${id}/questions`, data),
  updateQuestion: (qId, data) => api.put(`/assessments/questions/${qId}`, data),
  deleteQuestion: (qId) => api.delete(`/assessments/questions/${qId}`),
  addQuestionsBulk: (id, data) => api.post(`/assessments/${id}/questions/bulk`, data),
  startAttempt: (id, data) => api.post(`/assessments/${id}/start`, data),
  saveAnswer: (attemptId, data) => api.post(`/assessments/attempts/${attemptId}/save-answer`, data),
  saveAllAnswers: (attemptId, data) => api.post(`/assessments/attempts/${attemptId}/save-all-answers`, data),
  saveProgress: (attemptId, data) => api.post(`/assessments/attempts/${attemptId}/progress`, data),
  submitAttempt: (attemptId, data) => api.post(`/assessments/attempts/${attemptId}/submit`, data),
  recordProctorEvent: (attemptId, data) => api.post(`/assessments/attempts/${attemptId}/proctor-event`, data),
  getAttempt: (attemptId) => api.get(`/assessments/attempts/${attemptId}`),
  getAttemptAnswers: (attemptId) => api.get(`/assessments/attempts/${attemptId}/answers`),
  getStudentAssessments: (studentId) => api.get(`/assessments/student/${studentId}/assessments`),
  getMyAssessments: () => api.get('/assessments/student/list'),
  getAssessmentResults: (id) => api.get(`/assessments/${id}/results`),
  getResult: (resultId) => api.get(`/assessments/results/${resultId}`),
  getStudentResults: (studentId) => api.get(`/assessments/student/${studentId}/results`),
  getAssessmentRankings: (id) => api.get(`/assessments/${id}/rankings`),
  getEssayReview: (id) => api.get(`/assessments/${id}/essay-review`),
  gradeEssayAnswer: (answerId, data) => api.put(`/assessments/answers/${answerId}/grade`, data),
  getPendingEssayCount: () => api.get('/assessments/essay/pending-count'),
  getTeacherDashboard: (teacherId) => api.get(`/assessments/teacher/${teacherId}/dashboard`),
  // Mark Reclaims / Appeals
  createReclaim: (attemptId, answerId, data) =>
    api.post(`/assessments/attempts/${attemptId}/answers/${answerId}/reclaim`, data),
  getMyReclaims: (params) => api.get('/assessments/reclaims/mine', { params }),
  cancelReclaim: (reclaimId) => api.delete(`/assessments/reclaims/${reclaimId}`),
  getReclaimsForReview: (params) => api.get('/assessments/reclaims/review', { params }),
  getPendingReclaimCount: () => api.get('/assessments/reclaims/pending-count'),
  resolveReclaim: (reclaimId, data) => api.post(`/assessments/reclaims/${reclaimId}/resolve`, data),
  // Secure Exam Mode
  startAttemptSecure: (id, data, secureToken) => api.post(`/assessments/${id}/start`, data, {
    headers: secureToken ? { 'X-Secure-Exam-Token': secureToken } : {},
  }),
  // Composition / written-response review
  getCompositionReview: (id) => api.get(`/assessments/${id}/composition-review`),
  getCompositionPendingCount: () => api.get('/assessments/composition-review/pending-count'),
  approveCompositionAnswer: (answerId) => api.put(`/assessments/composition-answers/${answerId}/approve`),
  overrideCompositionAnswer: (answerId, data) => api.put(`/assessments/composition-answers/${answerId}/override`, data),
  editCompositionCriteria: (answerId, data) => api.put(`/assessments/composition-answers/${answerId}/criteria`, data),
  addCompositionFeedback: (answerId, data) => api.put(`/assessments/composition-answers/${answerId}/feedback`, data),
  retryCompositionGrading: (answerId) => api.post(`/assessments/composition-answers/${answerId}/retry`),
  manualGradeCompositionAnswer: (answerId, data) => api.put(`/assessments/composition-answers/${answerId}/manual-grade`, data),
  rejectCompositionAnswer: (answerId) => api.put(`/assessments/composition-answers/${answerId}/reject`),
  // Video assessment endpoints
  // Videos can be up to 1.5GB (see MAX_VIDEO_SIZE in the backend) and take
  // a long time to upload on a slow connection, so these need more than the
  // default 30s timeout - same pattern as homeworkAPI's file uploads above.
  // Matches nginx's client_max_body_size (1536M) / *_timeout for these paths
  // in deploy/nginx-bmcl.conf, nginx/bmcl.conf, and nginx/default.conf -
  // keep all four in sync.
  uploadVideo: (assessmentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/assessments/${assessmentId}/upload-video`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 3600000,
    });
  },
  deleteVideo: (assessmentId) => api.delete(`/assessments/${assessmentId}/video`),
  // Question video endpoints
  uploadQuestionVideo: (questionId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/assessments/questions/${questionId}/upload-video`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 3600000,
    });
  },
  deleteQuestionVideo: (questionId) => api.delete(`/assessments/questions/${questionId}/video`),
  // Uploads an image for a matching question's pair/distractor and returns a
  // persistent { url } that gets stored inside matching_data.
  uploadMatchingImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/assessments/questions/matching-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  // Uploads the picture for an image-based question. Returns { url, filename }
  // to store on the question (image_url / image_filename) before saving it.
  uploadQuestionImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/assessments/questions/image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  // Analytics & Export endpoints
  getAnalytics: (assessmentId) => api.get(`/assessments/${assessmentId}/analytics`),
  exportCSV: (assessmentId) => api.get(`/assessments/${assessmentId}/export-csv`, { responseType: 'blob' }),
  // Word (.docx) revision sheet / answer key. includeAnswers=true gives the
  // full key (teachers always; students only once "show correct answers" is on).
  exportRevisionDocx: (assessmentId, includeAnswers = true) =>
    api.get(`/assessments/${assessmentId}/export-docx`, {
      params: { include_answers: includeAnswers },
      responseType: 'blob',
      // Building a Word file with dozens of embedded images can take a while.
      timeout: 180000,
    }),
  getStudentResultsDetail: (assessmentId) => api.get(`/assessments/${assessmentId}/student-results`),
  resetAttemptForRetake: (attemptId) => api.delete(`/assessments/attempts/${attemptId}/reset-for-retake`),
  resetAllAttemptsForRetake: (assessmentId) => api.delete(`/assessments/${assessmentId}/reset-all-for-retake`),
  // Timing enforcement endpoints
  getServerTime: () => api.get('/assessments/server-time'),
  getTimingStatus: (assessmentId) => api.get(`/assessments/${assessmentId}/timing-status`),
  // Monitoring & Dashboard endpoints
  getMonitorSummary: (assessmentId) => api.get(`/assessments/${assessmentId}/monitor`),
  getMonitorStudents: (assessmentId, params) => api.get(`/assessments/${assessmentId}/monitor/students`, { params }),
  getNotAttempted: (assessmentId) => api.get(`/assessments/${assessmentId}/monitor/not-attempted`),
  getLiveStudents: (assessmentId) => api.get(`/assessments/${assessmentId}/monitor/live`),
  getPreview: (assessmentId, showAnswers) => api.get(`/assessments/${assessmentId}/preview`, { params: { show_answers: showAnswers } }),
  getSubmissionDetail: (assessmentId, studentId) => api.get(`/assessments/${assessmentId}/submissions/${studentId}`),
  sendReminder: (assessmentId, data) => api.post(`/assessments/${assessmentId}/send-reminder`, data),
  exportFullReport: (assessmentId, format) => api.get(`/assessments/${assessmentId}/export-full`, { params: { format }, responseType: 'blob' }),
  getSecureQuestions: (assessmentId) => api.get(`/assessments/${assessmentId}/secure-questions`),
  logScreenshot: (data) => api.post('/assessments/log-screenshot', data),
  getScreenshotLogs: (assessmentId, params) => api.get(`/assessments/${assessmentId}/screenshot-logs`, { params }),
  // Assessment Room / Student Readiness
  getParticipants: (assessmentId) => api.get(`/assessments/${assessmentId}/participants`),
  markParticipants: (assessmentId, data) => api.post(`/assessments/${assessmentId}/participants/mark`, data),
};

export const attendanceOverrideAPI = {
  list: (params) => api.get('/attendance-overrides', { params }),
  create: (data) => api.post('/attendance-overrides', data),
  update: (id, data) => api.put(`/attendance-overrides/${id}`, data),
  remove: (id) => api.delete(`/attendance-overrides/${id}`),
  toggle: (id) => api.patch(`/attendance-overrides/${id}/toggle`),
  searchPersons: (params) => api.get('/attendance-overrides/search-persons', { params }),
  summary: () => api.get('/attendance-overrides/reports/summary'),
  byPerson: (personType, personId) => api.get(`/attendance-overrides/person/${personType}/${personId}`),
};

export const attendanceReasonsAPI = {
  list: (params) => api.get('/attendance-reasons/requests', { params }),
  stats: () => api.get('/attendance-reasons/stats'),
  get: (id) => api.get(`/attendance-reasons/requests/${id}`),
  submit: (data) => api.post('/attendance-reasons/submit', data),
  approve: (id, data) => api.put(`/attendance-reasons/requests/${id}/approve`, data),
  reject: (id, data) => api.put(`/attendance-reasons/requests/${id}/reject`, data),
  requestMoreInfo: (id, data) => api.put(`/attendance-reasons/requests/${id}/more-info`, data),
  myRequests: (params) => api.get('/attendance-reasons/my-requests', { params }),
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/attendance-reasons/upload-attachment', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const liveClassAPI = {
  // Teacher
  create: (data) => api.post('/live-classes', data),
  update: (id, data) => api.put(`/live-classes/${id}`, data),
  // Creates the real Google Meet space for this lesson (school's connected
  // Google account) - required before publish.
  createMeet: (id) => api.post(`/live-classes/${id}/create-meet`),
  publish: (id) => api.post(`/live-classes/${id}/publish`),
  cancel: (id, data) => api.post(`/live-classes/${id}/cancel`, data || {}),
  start: (id) => api.post(`/live-classes/${id}/start`),
  end: (id) => api.post(`/live-classes/${id}/end`),
  remove: (id) => api.delete(`/live-classes/${id}`),
  getTeacherList: (params) => api.get('/live-classes/teacher', { params }),
  getTeacherDashboard: () => api.get('/live-classes/teacher/dashboard'),
  getParticipants: (id) => api.get(`/live-classes/${id}/participants`),
  lock: (id) => api.post(`/live-classes/${id}/lock`),
  unlock: (id) => api.post(`/live-classes/${id}/unlock`),
  getReport: (id) => api.get(`/live-classes/${id}/report`),
  // status: not_joined | attended | partial | absent | late | excused -
  // always a manual, audited decision, never inferred from a mere join.
  setAttendance: (id, studentId, status, reason) =>
    api.patch(`/live-classes/${id}/participants/${studentId}/attendance`, { status, reason }),
  // Take a student off the EduPremier attendance roster for this session
  // (NOT a Google Meet kick). Soft + restorable; audited.
  removeParticipant: (id, studentId, reason) =>
    api.post(`/live-classes/${id}/participants/${studentId}/remove`, { reason }),
  restoreParticipant: (id, studentId) =>
    api.post(`/live-classes/${id}/participants/${studentId}/restore`),
  setParticipantNote: (id, studentId, note) =>
    api.patch(`/live-classes/${id}/participants/${studentId}/note`, { note }),
  sendAnnouncement: (id, message) => api.post(`/live-classes/${id}/announcement`, { message }),
  reportIssue: (id, message) => api.post(`/live-classes/${id}/report-issue`, { message }),
  getResources: (id) => api.get(`/live-classes/${id}/resources`),
  addResource: (id, title, url) => api.post(`/live-classes/${id}/resources`, { title, url }),
  removeResource: (id, resourceId) => api.delete(`/live-classes/${id}/resources/${resourceId}`),
  // Screen recordings: the teacher records their own browser tab (getDisplayMedia
  // + MediaRecorder) and uploads the .webm here. Students of this session can
  // watch it only after the class has ended. The <video> tag can't set an
  // Authorization header, so the stream URL carries ?token=<accessToken>.
  getRecordings: (id) => api.get(`/live-classes/${id}/recordings`),
  uploadRecording: (id, blob, durationSeconds, onUploadProgress) => {
    const fd = new FormData();
    fd.append('file', blob, `recording-${Date.now()}.webm`);
    if (durationSeconds != null) fd.append('duration_seconds', String(Math.round(durationSeconds)));
    return api.post(`/live-classes/${id}/recordings`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress,
    });
  },
  deleteRecording: (id, recordingId) => api.delete(`/live-classes/${id}/recordings/${recordingId}`),
  recordingStreamUrl: (streamUrl) => {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1$/, '');
    const token = localStorage.getItem('accessToken') || '';
    return `${base}${streamUrl}${streamUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
  },
  // Student
  getStudentList: (bucket) => api.get('/live-classes/student/list', { params: { bucket } }),
  // Records the join attempt and returns the real google_meet_uri to open
  // in a new tab - the meeting itself is never embedded in this app.
  join: (id) => api.post(`/live-classes/${id}/join`),
  getMyParticipationHistory: () => api.get('/live-classes/student/participation-history'),
  // Shared
  getDetail: (id) => api.get(`/live-classes/${id}`),
  // Admin
  adminList: (params) => api.get('/live-classes/admin/list', { params }),
  adminCancel: (id, data) => api.post(`/live-classes/admin/${id}/cancel`, data || {}),
  getAdminOverview: () => api.get('/live-classes/admin/overview'),
  getAttendanceRules: () => api.get('/live-classes/admin/attendance-rules'),
  updateAttendanceRules: (data) => api.put('/live-classes/admin/attendance-rules', data),
};

// School-wide Google Meet connection (admin-only) - see google_meet_service.py
// for why this is one shared account rather than per-teacher OAuth.
export const googleAuthAPI = {
  getAuthorizationUrl: () => api.get('/google/connect'),
  getStatus: () => api.get('/google/status'),
  disconnect: () => api.delete('/google/disconnect'),
};

export const secureExamAPI = {
  // policy list management (admin)
  listPolicies: (params) => api.get('/secure-exam/policies', { params }),
  createPolicy: (data) => api.post('/secure-exam/policies', data),
  updatePolicy: (id, data) => api.put(`/secure-exam/policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/secure-exam/policies/${id}`),
  // policy bundle for an exam (student prep / secure client)
  getPolicyBundle: (assessmentId) => api.get(`/secure-exam/policy-bundle/${assessmentId}`),
  // secure session lifecycle (student)
  createSession: (data) => api.post('/secure-exam/sessions', data),
  heartbeat: (sessionId, currentQuestionId) => api.post(`/secure-exam/sessions/${sessionId}/heartbeat`, null,
    currentQuestionId ? { params: { current_question_id: currentQuestionId } } : undefined),
  postEvent: (sessionId, data) => api.post(`/secure-exam/sessions/${sessionId}/event`, data),
  endSession: (sessionId, reason) => api.post(`/secure-exam/sessions/${sessionId}/end`, null, { params: { reason } }),
  // monitoring (teacher / admin)
  monitor: (assessmentId) => api.get(`/secure-exam/monitor/${assessmentId}`),
  events: (assessmentId, params) => api.get(`/secure-exam/events/${assessmentId}`, { params }),
};

export default api;
